import axios from 'axios';
import {HubriseApiContract} from 'common/api/hubrise/service/HubriseApiContract';
import {HubriseOrderContract} from 'common/api/hubrise/model/order/HubriseOrderContract';
import {HubriseOrderStatus} from 'common/api/hubrise/model/order/HubriseOrderStatus';
import {HubriseMenuCatalogContract} from 'common/api/hubrise/model/menu/HubriseMenuCatalogContract';
import {ExternalApiException} from 'common/exception/ExternalApiException';
import {Logger} from 'common/tool/Logger';
import {Retry} from 'common/tool/Retry';
import {HubriseCustomerContract} from 'common/api/hubrise/model/customer/HubriseCustomerContract';
import {HubriseCustomerListContract} from 'common/api/hubrise/model/customer/HubriseCustomerListContract';
import {HubriseLocationContract} from 'common/api/hubrise/model/location/HubriseLocationContract';
import {HubriseUserContract} from 'common/api/hubrise/model/user/HubriseUserContract';
import * as querystring from 'querystring';
import {HubriseAuthContract} from 'common/api/hubrise/model/auth/HubriseAuthContract';
import {HubriseResourceType} from 'common/api/hubrise/model/callback/HubriseResourceType';
import {HubriseEventType} from 'common/api/hubrise/model/callback/HubriseEventType';
import {HubriseAuthResource} from 'common/api/hubrise/model/auth/HubriseAuthResource';
import {HubriseRightAccess} from 'common/api/hubrise/model/auth/HubriseRightAccess';
import {ValidatorUtil} from 'common/util/ValidatorUtil';
import {HubriseAccountContract} from 'common/api/hubrise/model/account/HubriseAccountContract';
import {HubriseAccessTokenContract} from 'common/api/hubrise/model/auth/HubriseAccessTokenContract';
import {HubriseAccessLevel} from 'common/api/hubrise/model/auth/HubriseAccessLevel';
import {BadParameterException} from 'common/exception/BadParameterException';
import {HubriseCallbackContract} from 'common/api/hubrise/model/callback/HubriseCallbackContract';

export class HubriseApi implements HubriseApiContract {
    public static readonly BASE_URL: string = 'http://api.hubrise.com/v1/';
    public static readonly BASE_AUTH_URL: string = 'https://manager.hubrise.com/oauth2/v1/';
    private static readonly TIMEOUT: number = 15000;
    private readonly accessToken: HubriseAccessTokenContract;

    public constructor(accessToken?: HubriseAccessTokenContract) {
        this.accessToken = accessToken;
    }

    public async authorize(name: string, clientId: string, redirect: string, accessLevel: HubriseAccessLevel, rights?: Partial<Record<HubriseAuthResource, HubriseRightAccess>>,
                           profile: boolean = false, appInstanceId?: string): Promise<string> {
        return HubriseApi.BASE_AUTH_URL + 'authorize?' +
            (name !== undefined ? 'name=' + String(name) + '&' : '') +
            'redirect_uri=' + encodeURIComponent(redirect + '&accessLevel=' + String(accessLevel || HubriseAccessLevel.LOCATION)) + '&' +
            'client_id=' + clientId + '&' +
            'scope=' + ([HubriseAccessLevel.ACCOUNT, HubriseAccessLevel.LOCATION].includes(accessLevel) ? String(accessLevel)
                + '[' + String(Object.keys(rights).reduce((results, key) => results.concat(key + '.' + String((rights as any)[key])), []).join(',')) + ']' : '')
            + ([HubriseAccessLevel.ACCOUNT, HubriseAccessLevel.LOCATION].includes(accessLevel) && (accessLevel === HubriseAccessLevel.PROFILE || profile) ? ',' : '')
            + (accessLevel === HubriseAccessLevel.PROFILE || profile ? 'profile_with_email' : '') + '&'
            + (appInstanceId === undefined ? '' : 'app_instance_id=' + String(appInstanceId));
    }

    public async getToken(clientId: string, clientSecret: string, code: string): Promise<HubriseAuthContract> {
        let auth: HubriseAuthContract;
        await Retry.request(async () => {
            const response: any = await axios.post(HubriseApi.BASE_AUTH_URL + 'token', querystring.stringify({
                code: code,
                client_id: clientId,
                client_secret: clientSecret
            }), {
                headers: {'Content-Type': 'application/x-www-form-urlencoded'},
                timeout: HubriseApi.TIMEOUT
            });

            if (response === undefined || response.data === undefined) {
                throw new ExternalApiException('failed to get hubrise token');
            }

            auth = response.data;
        }, 1, Logger.error.bind(Logger));

        return auth;
    }

    public async getUser(): Promise<HubriseUserContract> {
        let hubriseUser: HubriseUserContract;
        await Retry.request(async () => {
            const response: any = await axios.get(HubriseApi.BASE_URL + 'user', {
                headers: {'X-Access-Token': this.accessToken.token},
                timeout: HubriseApi.TIMEOUT
            });

            if (response === undefined || response.data === undefined) {
                throw new ExternalApiException('failed to retrieve hubrise user');
            }

            hubriseUser = response.data;
        }, 1, Logger.error.bind(Logger));

        return hubriseUser;
    }

    public async getAccount(): Promise<HubriseAccountContract> {
        if (this.accessToken.access === HubriseAccessLevel.LOCATION) {
            const location: HubriseLocationContract = await this.getLocation();

            return location.account;
        }

        let hubriseAccount: HubriseAccountContract;
        await Retry.request(async () => {
            const response: any = await axios.get(HubriseApi.BASE_URL + 'account', {
                headers: {'X-Access-Token': this.accessToken.token},
                timeout: HubriseApi.TIMEOUT
            });

            if (response === undefined || response.data === undefined) {
                throw new ExternalApiException('failed to retrieve hubrise account');
            }

            hubriseAccount = response.data;
        }, 1, Logger.error.bind(Logger));

        return hubriseAccount;
    }

    public async getLocations(): Promise<HubriseLocationContract[]> {
        if (this.accessToken.access === HubriseAccessLevel.LOCATION) {
            return [await this.getLocation()];
        }

        let hubriseLocations: HubriseLocationContract[];
        await Retry.request(async () => {
            const response: any = await axios.get(HubriseApi.BASE_URL + 'locations', {
                headers: {'X-Access-Token': this.accessToken.token},
                timeout: HubriseApi.TIMEOUT
            });

            if (response === undefined || response.data === undefined) {
                throw new ExternalApiException('failed to retrieve hubrise locations');
            }

            hubriseLocations = response.data;
        }, 1, Logger.error.bind(Logger));

        return hubriseLocations;
    }

    public async getLocation(hubriseLocationId?: string): Promise<HubriseLocationContract> {
        let hubriseLocation: HubriseLocationContract;
        await Retry.request(async () => {
            const url: string = this.accessToken.access === HubriseAccessLevel.ACCOUNT ? 'locations/' + String(hubriseLocationId) : 'location';
            const response: any = await axios.get(HubriseApi.BASE_URL + url, {
                headers: {'X-Access-Token': this.accessToken.token},
                timeout: HubriseApi.TIMEOUT
            });

            if (response === undefined || response.data === undefined) {
                throw new ExternalApiException('failed to retrieve hubrise location');
            }

            hubriseLocation = response.data;
        }, 1, Logger.error.bind(Logger));

        return hubriseLocation;
    }

    public async createCustomer(hubriseCustomerListId: string, hubriseCustomer: HubriseCustomerContract): Promise<HubriseCustomerContract> {
        let createdCustomer: HubriseCustomerContract;
        await Retry.request(async () => {
            const response: any = await axios.post(HubriseApi.BASE_URL + 'customer_lists/' + String(hubriseCustomerListId) + '/customers', hubriseCustomer, {
                headers: {'X-Access-Token': this.accessToken.token},
                timeout: HubriseApi.TIMEOUT
            });

            if (response === undefined || response.data === undefined) {
                throw new ExternalApiException('failed to create hubrise customer');
            }

            createdCustomer = response.data;
        }, 1, Logger.error.bind(Logger));

        return createdCustomer;
    }

    public async updateCustomer(hubriseCustomerListId: string, hubriseCustomer: HubriseCustomerContract): Promise<HubriseCustomerContract> {
        if (typeof hubriseCustomer.id !== 'string') {
            throw new BadParameterException('missing customer id', {customer: hubriseCustomer});
        }

        let createdCustomer: HubriseCustomerContract;
        await Retry.request(async () => {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const {id, ...customer} = hubriseCustomer;
            const response: any = await axios.put(HubriseApi.BASE_URL + 'customer_lists/' + String(hubriseCustomerListId) + '/customers/' + String(hubriseCustomer.id), customer, {
                headers: {'X-Access-Token': this.accessToken.token},
                timeout: HubriseApi.TIMEOUT
            });

            if (response === undefined || response.data === undefined) {
                throw new ExternalApiException('failed to update hubrise customer');
            }

            createdCustomer = response.data;
        }, 1, Logger.error.bind(Logger));

        return createdCustomer;
    }

    public async getCustomer(hubriseCustomerListId: string, hubriseCustomerId: string): Promise<HubriseCustomerContract> {
        let hubriseCustomer: HubriseCustomerContract;
        await Retry.request(async () => {
            const response: any = await axios.get(HubriseApi.BASE_URL + 'customer_lists/' + String(hubriseCustomerListId) + '/customers/' + String(hubriseCustomerId), {
                headers: {'X-Access-Token': this.accessToken.token},
                timeout: HubriseApi.TIMEOUT
            });

            if (response === undefined || response.data === undefined) {
                throw new ExternalApiException('failed to retrieve hubrise customer');
            }

            hubriseCustomer = response.data;
        }, 1, Logger.error.bind(Logger));

        return hubriseCustomer;
    }

    public async getCustomers(hubriseCustomerListId: string, reference?: string, email?: string, phone?: string): Promise<HubriseCustomerContract[]> {
        const parameters: URLSearchParams = new URLSearchParams();
        if (typeof reference === 'string') {
            parameters.append('private_ref', reference);
        }
        if (typeof email === 'string') {
            parameters.append('email', email + '*');
        }
        if (typeof phone === 'string') {
            parameters.append('phone', phone + '*');
        }

        let hubriseCustomers: HubriseCustomerContract[];
        await Retry.request(async () => {
            const response: any = await axios.get(HubriseApi.BASE_URL + 'customer_lists/' + String(hubriseCustomerListId) + '/customers?' + parameters.toString(), {
                headers: {'X-Access-Token': this.accessToken.token},
                timeout: HubriseApi.TIMEOUT
            });

            if (response === undefined || response.data === undefined) {
                throw new ExternalApiException('failed to retrieve hubrise customers');
            }

            hubriseCustomers = response.data;
        }, 1, Logger.error.bind(Logger));

        return hubriseCustomers;
    }

    public async getCustomerLists(hubriseLocationId: string): Promise<HubriseCustomerListContract[]> {
        let hubriseCustomerLists: HubriseCustomerListContract[];
        await Retry.request(async () => {
            const response: any = await axios.get(HubriseApi.BASE_URL + 'locations/' + String(hubriseLocationId) + '/customer_lists', {
                headers: {'X-Access-Token': this.accessToken.token},
                timeout: HubriseApi.TIMEOUT
            });

            if (response === undefined || response.data === undefined) {
                throw new ExternalApiException('failed to retrieve hubrise customer lists');
            }

            hubriseCustomerLists = response.data;
        }, 1, Logger.error.bind(Logger));

        return hubriseCustomerLists;
    }

    public async createOrder(hubriseLocationId: string, hubriseOrder: HubriseOrderContract): Promise<HubriseOrderContract> {
        let createdOrder: HubriseOrderContract;
        await Retry.request(async () => {
            const response: any = await axios.post(HubriseApi.BASE_URL + 'locations/' + String(hubriseLocationId) + '/orders', hubriseOrder, {
                headers: {'X-Access-Token': this.accessToken.token},
                timeout: HubriseApi.TIMEOUT
            });

            if (response === undefined || response.data === undefined) {
                throw new ExternalApiException('failed to create hubrise order');
            }

            createdOrder = response.data;
        }, 1, Logger.error.bind(Logger));

        return createdOrder;
    }

    public async getOrder(hubriseLocationId: string, hubriseOrderId: string): Promise<HubriseOrderContract> {
        let hubriseOrder: HubriseOrderContract;
        await Retry.request(async () => {
            const response: any = await axios.get(HubriseApi.BASE_URL + 'locations/' + String(hubriseLocationId) + '/orders/' + String(hubriseOrderId), {
                headers: {'X-Access-Token': this.accessToken.token},
                timeout: HubriseApi.TIMEOUT
            });

            if (response === undefined || response.data === undefined) {
                throw new ExternalApiException('failed to retrieve hubrise order');
            }

            hubriseOrder = response.data;
        }, 1, Logger.error.bind(Logger));

        return hubriseOrder;
    }

    public async getOrders(hubriseLocationId: string, startDate?: string, endDate?: string, status?: HubriseOrderStatus, limit: number = 30): Promise<HubriseOrderContract[]> {
        let hubriseOrders: HubriseOrderContract[] = [];

        const queryParams = new URLSearchParams();
        queryParams.append('count', String(limit));
        if (ValidatorUtil.isISODate(startDate)) {
            queryParams.append('after', startDate);
        }
        if (ValidatorUtil.isISODate(endDate)) {
            queryParams.append('before', endDate);
        }
        if (status !== undefined) {
            queryParams.append('status', String(status));
        }
        await Retry.request(async () => {
            const response: any = await axios.get(HubriseApi.BASE_URL + 'locations/' + String(hubriseLocationId) + '/orders?' + queryParams.toString(), {
                headers: {'X-Access-Token': this.accessToken.token},
                timeout: HubriseApi.TIMEOUT
            });

            if (response === undefined || response.data === undefined) {
                throw new ExternalApiException('failed to retrieve hubrise orders');
            }

            hubriseOrders = response.data;
        }, 1, Logger.error.bind(Logger));

        return hubriseOrders.sort((a, b) => a.created_at.localeCompare(b.created_at));
    }

    public async setOrderStatus(hubriseLocationId: string, hubriseOrderId: string, status: HubriseOrderStatus): Promise<void> {
        await Retry.request(async () => {
            const response: any = await axios.patch(HubriseApi.BASE_URL + 'locations/' + String(hubriseLocationId) + '/orders/' + String(hubriseOrderId), {
                status: status
            }, {
                headers: {'X-Access-Token': this.accessToken.token},
                timeout: HubriseApi.TIMEOUT
            });

            if (response === undefined || response.data === undefined) {
                throw new ExternalApiException('failed to update hubrise order status');
            }
        }, 1, Logger.error.bind(Logger));
    }

    public async getCatalog(hubriseCatalogId: string): Promise<HubriseMenuCatalogContract> {
        let catalog: HubriseMenuCatalogContract;
        await Retry.request(async () => {
            const response: any = await axios.get(HubriseApi.BASE_URL + 'catalogs/' + String(hubriseCatalogId), {
                headers: {'X-Access-Token': this.accessToken.token},
                timeout: HubriseApi.TIMEOUT
            });

            if (response === undefined || response.data === undefined) {
                throw new ExternalApiException('failed to retrieve hubrise catalogs');
            }

            catalog = response.data;
        }, 1, Logger.error.bind(Logger));

        return catalog;
    }

    public async getCatalogs(hubriseLocationId: string): Promise<HubriseMenuCatalogContract[]> {
        let catalogs: HubriseMenuCatalogContract[] = [];
        await Retry.request(async () => {
            const response: any = await axios.get(HubriseApi.BASE_URL + 'locations/' + String(hubriseLocationId) + '/catalogs', {
                headers: {'X-Access-Token': this.accessToken.token},
                timeout: HubriseApi.TIMEOUT
            });

            if (response === undefined || response.data === undefined) {
                throw new ExternalApiException('failed to retrieve hubrise catalogs');
            }

            catalogs = response.data;
        }, 1, Logger.error.bind(Logger));

        return catalogs;
    }

    public async updateWebhooks(url: string, events: Partial<Record<HubriseResourceType, HubriseEventType[]>>): Promise<void> {
        await Retry.request(async () => {
            const response: any = await axios.post(HubriseApi.BASE_URL + '/callback', {
                url: url,
                events: events
            }, {
                headers: {'X-Access-Token': this.accessToken.token},
                timeout: HubriseApi.TIMEOUT
            });

            if (response === undefined || response.data === undefined) {
                throw new ExternalApiException('failed to create hubrise webhook');
            }
        }, 1, Logger.error.bind(Logger));
    }

    public async getWebhooks(): Promise<HubriseCallbackContract> {
        let webhooks: HubriseCallbackContract;
        await Retry.request(async () => {
            const response: any = await axios.get(HubriseApi.BASE_URL + '/callback', {
                headers: {'X-Access-Token': this.accessToken.token},
                timeout: HubriseApi.TIMEOUT
            });

            if (response === undefined || response.data === undefined) {
                throw new ExternalApiException('failed to get hubrise webhook');
            }

            webhooks = response.data;
        }, 1, Logger.error.bind(Logger));

        return webhooks;
    }

    public async deleteWebhooks(): Promise<void> {
        await Retry.request(async () => {
            await axios.delete(HubriseApi.BASE_URL + '/callback', {
                headers: {'X-Access-Token': this.accessToken.token},
                timeout: HubriseApi.TIMEOUT
            });
        }, 1, Logger.error.bind(Logger));
    }
}
